import { useEffect, useState } from 'react'
import { useChainId } from 'wagmi'

import { EModeCategory, PositionOverview } from '../types'
import { createEModeObjectives } from './createEModeObjectives'
import { getEModeCategories } from './getEModeCategories'
import { getUpdatedPositionOverview } from './getUpdatedPositionOverview'
import { getNativeAssetInfo } from '@/config/chain/utils/getNativeAssetInfo'
import { useActions } from '@/features/actions/logic/useActions'
import { ActionsViewState } from '@/features/actions/types'
import { PageState, PageStatus } from '@/features/dialogs/common/types'
import { useAaveDataLayer } from '@/features/sparklend/common/data/market-info/aave-data-layer/useAaveDataLayer'
import {
  EModeCategoryId,
  EModeCategoryName,
  eModeCategoryIdToName,
} from '@/features/sparklend/common/data/market-info/emode'
import { useMarketInfo } from '@/features/sparklend/common/data/market-info/useMarketInfo'
import { RiskAcknowledgementInfo } from '@/features/sparklend/common/utils/liquidation-risk-warning/types'
import { useLiquidationRiskWarning } from '@/features/sparklend/common/utils/liquidation-risk-warning/useLiquidationRiskWarning'
import {
  getValidateSetUserEModeArgs,
  SetUserEModeValidationIssue,
  validateSetUserEMode,
} from '@/features/sparklend/common/validation/validateSetUserEMode'

interface UseEModeDialogParams {
  userEModeCategoryId: EModeCategoryId
}

export interface UseEModeDialogResult {
  eModeCategories: Record<EModeCategoryName, EModeCategory>
  selectedEModeCategoryName: EModeCategoryName
  actionsViewState: ActionsViewState
  showActions: boolean
  validationIssue?: SetUserEModeValidationIssue
  currentPositionOverview: PositionOverview
  updatedPositionOverview?: PositionOverview
  pageStatus: PageStatus
  riskAcknowledgement: RiskAcknowledgementInfo
}

export function useEModeDialog({ userEModeCategoryId }: UseEModeDialogParams): UseEModeDialogResult {
  const chainId = useChainId()
  const { aaveData } = useAaveDataLayer({ chainId })
  const { marketInfo } = useMarketInfo({ chainId })
  const nativeAssetInfo = getNativeAssetInfo(marketInfo.chainId)

  const [pageStatus, setPageStatus] = useState<PageState>('form')
  const [selectedEModeCategoryId, setSelectedEModeCategoryId] = useState<EModeCategoryId>(userEModeCategoryId)

  const currentPositionOverview = {
    healthFactor: marketInfo.userPositionSummary.healthFactor,
    maxLTV: marketInfo.userPositionSummary.maxLoanToValue,
  }

  const updatedPositionOverview = getUpdatedPositionOverview({
    marketInfo,
    aaveData,
    currentEModeCategoryId: userEModeCategoryId,
    selectedEModeCategoryId,
    nativeAssetInfo,
  })

  const eModeCategories = getEModeCategories(marketInfo, selectedEModeCategoryId, setSelectedEModeCategoryId)

  const actions = createEModeObjectives(userEModeCategoryId, selectedEModeCategoryId)

  const validationIssue = updatedPositionOverview
    ? validateSetUserEMode(
        getValidateSetUserEModeArgs({
          requestedEModeCategoryId: selectedEModeCategoryId,
          healthFactorAfterChangingEMode: updatedPositionOverview.healthFactor,
          marketInfo,
        }),
      )
    : undefined

  const { riskAcknowledgement, disableActionsByRisk } = useLiquidationRiskWarning({
    type: 'liquidation-warning-e-mode-off',
    isFormValid: !validationIssue,
    currentHealthFactor: currentPositionOverview.healthFactor,
    updatedHealthFactor: updatedPositionOverview?.healthFactor,
  })

  const actionsEnabled = !validationIssue && !disableActionsByRisk

  const { viewState: actionsViewState, reset: resetActions } = useActions({
    objectives: actions ?? [],
    context: {
      chainId,
    },
    onFinish: () => setPageStatus('success'),
    enabled: actionsEnabled,
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: we want to reset actions when the form values change
  useEffect(() => {
    resetActions()
  }, [selectedEModeCategoryId, resetActions])

  return {
    eModeCategories,
    selectedEModeCategoryName: eModeCategoryIdToName[selectedEModeCategoryId],
    actionsViewState,
    showActions: Boolean(actions?.length) && actionsViewState.type !== 'no-actions',
    validationIssue,
    currentPositionOverview,
    updatedPositionOverview,
    pageStatus: {
      state: pageStatus,
      actionsEnabled,
      goToSuccessScreen: () => setPageStatus('success'),
    },
    riskAcknowledgement,
  }
}
